/**
 * @file ListingsView.jsx
 * @description İlan listesi görünüm bileşeni
 *
 * Inline modda ilan aramalarının sonuçlarını gösterir.
 * Başlık, filtreler, ilan kartları ve sayfalama içerir.
 * "Harita'da Aç" butonu ile fullscreen moda geçiş sağlar.
 *
 * @module widget/components/ListingsView
 */

import { memo } from "react";
import { Maximize2 } from "lucide-react";
import { Button } from "@openai/apps-sdk-ui/components/Button";
import { FiltersSidebar } from "./FiltersSidebar";
import { CompactListingCard } from "./CompactListingCard";
import { LoadMoreButton } from "./LoadMoreButton";
import { SortDropdown } from "./SortDropdown";
import { openExternal, formatNumber } from "../utils";

/**
 * İlan Listesi Görünüm Bileşeni
 *
 * ChatGPT inline widget'ında ilan arama sonuçlarını
 * listeler. Filtre yönetimi, sayfalama ve fullscreen
 * geçiş özellikleri sunar.
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {Object[]} props.listings - İlan listesi
 * @param {number} props.total - Toplam sonuç sayısı
 * @param {number} props.page - Mevcut sayfa numarası
 * @param {Object} props.query - Arama sorgusu (path, filters)
 * @param {Object} props.filters - Aktif filtreler
 * @param {boolean} props.isDark - Karanlık mod
 * @param {boolean} props.isLoadingMore - Daha fazla yükleniyor
 * @param {Function} props.onRemoveFilter - Filtre kaldırma
 * @param {Function} props.onApplyFilter - Filtre uygulama
 * @param {Function} props.onDetailClick - Detay açma
 * @param {Function} props.onLoadMore - Daha fazla yükle callback
 * @param {Function} props.onRequestFullscreen - Fullscreen isteme
 * @param {string} [props.currentSort] - Mevcut sıralama değeri
 * @param {Function} [props.onSortChange] - Sıralama değiştiğinde çağrılır
 * @param {boolean} [props.isSorting] - Sıralama işlemi devam ediyor mu
 * @param {Set<string>} [props.savedListingIdSet] - Kaydedilen ilan ID'leri Set'i
 * @param {Function} [props.onToggleSave] - Kaydetme toggle callback'i
 * @param {boolean} [props.isSaveAtLimit] - Kaydetme limiti doldu mu
 * @param {Set<string>} [props.compareListingIdSet] - Karşılaştırmadaki ilan ID'leri Set'i
 * @param {Function} [props.onToggleCompare] - Karşılaştırma toggle callback'i
 * @param {boolean} [props.isCompareAtLimit] - Karşılaştırma limiti doldu mu
 *
 * @returns {JSX.Element} Liste görünüm bileşeni
 *
 * @example
 * <ListingsView
 *   listings={listings}
 *   total={150}
 *   page={1}
 *   query={{ path: "satilik-konut/istanbul" }}
 *   filters={{ "oda-sayisi": ["3-1"] }}
 *   isDark={false}
 *   onDetailClick={(id) => fetchDetail(id)}
 *   onLoadMore={() => loadNextPage()}
 *   onRequestFullscreen={() => requestFullscreen()}
 * />
 */
function ListingsViewInner({
  listings,
  total,
  page,
  query,
  filters,
  isDark,
  isLoadingMore,
  onRemoveFilter,
  onApplyFilter,
  onDetailClick,
  onLoadMore,
  onRequestFullscreen,
  currentSort = "relevance",
  onSortChange,
  isSorting = false,
  savedListingIdSet,
  onToggleSave,
  isSaveAtLimit = false,
  compareListingIdSet,
  onToggleCompare,
  isCompareAtLimit = false,
}) {
  const title = "İlanlar";
  const subtitle = total
    ? `${formatNumber(total)} sonuç • Sayfa ${page}`
    : "Sonuçları burada göreceksiniz";

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header - responsive layout */}
      <div
        className={`px-3 py-2 border-b flex flex-wrap items-center justify-between gap-2 ${
          isDark ? "border-white/10" : "border-black/10"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`text-xs font-medium rounded-full px-2 py-0.5 flex-shrink-0 ${
              isDark
                ? "bg-emerald-500/20 text-emerald-400"
                : "bg-emerald-500/10 text-emerald-700"
            }`}
          >
            {title}
          </span>
          <span
            className={`text-xs truncate ${isDark ? "text-white/60" : "text-black/60"}`}
          >
            {subtitle}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {onSortChange && (
            <SortDropdown
              value={currentSort}
              onSortChange={onSortChange}
              isDark={isDark}
              disabled={isSorting}
            />
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={onRequestFullscreen}
            title="Harita'da Aç"
          >
            <Maximize2 className="h-4 w-4" strokeWidth={1.5} />
            <span className="ml-1 hidden sm:inline">Harita'da Aç</span>
          </Button>
        </div>
      </div>

      {/* Filters + List */}
      <div className="flex flex-col overflow-hidden">
        <FiltersSidebar
          filters={filters}
          total={total}
          onRemoveFilter={onRemoveFilter}
          onApplyFilter={onApplyFilter}
          onAddFilter={() => {}}
          isDark={isDark}
        />

        <div className="flex-1 overflow-y-auto">
          {/* Listing Cards */}
          {listings.map((listing, index) => {
            const listingId =
              typeof listing?.id === "number" && Number.isFinite(listing.id)
                ? listing.id
                : null;
            const listingIdStr = listingId ? String(listingId) : null;
            const isSaved = listingIdStr && savedListingIdSet ? savedListingIdSet.has(listingIdStr) : false;
            const isInCompare = listingIdStr && compareListingIdSet ? compareListingIdSet.has(listingIdStr) : false;
            return (
              <CompactListingCard
                key={listingId ?? listing?.url ?? `listing-${index}`}
                listing={listing}
                onDetailClick={onDetailClick}
                isDark={isDark}
                openExternal={openExternal}
                isSaved={isSaved}
                onToggleSave={onToggleSave}
                canSave={!isSaveAtLimit || isSaved}
                isInCompare={isInCompare}
                onToggleCompare={onToggleCompare}
                canCompare={!isCompareAtLimit || isInCompare}
              />
            );
          })}

          {/* Load More */}
          {total > listings.length && (
            <LoadMoreButton
              total={total}
              currentCount={listings.length}
              isLoading={isLoadingMore}
              onLoadMore={onLoadMore}
              query={query}
              isDark={isDark}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Memoized ListingsView - Heavy component, memo ile sarmalandı
 * re-render'ları minimize etmek için.
 */
export const ListingsView = memo(ListingsViewInner);
export default ListingsView;
